import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { company } from '~/company/Company';
import Input from '~/components/isolated/Input';
import Modal from '~/components/Modal/Modal';
import { ModalBody } from '~/components/ModalComponents';
import { deliveryAddressStore } from '~/components/ModalDeliveryAddress/DeliveryAddressStore';
import { useGlobal } from '~/hooks/useGlobal';
import { MODAL_EDIT_ADDRESS_PARAM, useModal } from '~/hooks/useModal';
import { useSaveAddress } from '~/hooks/useSaveAddress';

import { orderStore, userStore } from '~/stores';

import { ModalFooter } from '../Modal/components/ModalFooter';
import { ModalHeader } from '../Modal/components/ModalHeader';
import { REDIRECT_QUERY_NAME } from '../ModalDeliveryAddress/constants';

import DeliveryInstructions from './DeliveryInstructions';
import MapStatic from './MapStatic/MapStatic';
import styles from './ModalEditAddressDetailsV3.module.scss';
import SaveButton from './SaveButton';

const ModalEditAddressDetailsV3 = observer(() => {
  const { t } = useTranslation();
  const { closeModal, goBack } = useModal();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isMobile } = useGlobal();
  const editableAddressId = searchParams.get(MODAL_EDIT_ADDRESS_PARAM);
  const redirectNext = searchParams.get(REDIRECT_QUERY_NAME);
  const geocodingRequestTimeoutRef =
    useRef<Nullable<ReturnType<typeof setTimeout>>>(null);

  // Функция, которая вызывается ПОСЛЕ успешного сохранения адреса
  const handleAfterSave = () => {
    if (!redirectNext) {
      closeModal([MODAL_EDIT_ADDRESS_PARAM, REDIRECT_QUERY_NAME]);
      return;
    }

    navigate(redirectNext, { replace: true });
  };

  // Хук для мобильной версии - получаем функцию сохранения и состояния
  const {
    handleSaveAddress: handleSaveAddressMobile,
    isLoading,
    isDisabled,
  } = useSaveAddress({
    onClick: handleAfterSave,
  });

  useEffect(() => {
    if (editableAddressId) {
      const editableAddress = userStore.addressList.find(
        (item) => item.id === editableAddressId,
      );
      if (editableAddress) {
        const point = {
          lat: editableAddress.latitude,
          lng: editableAddress.longitude,
        };

        const selectedZone = deliveryAddressStore.getSelectedPolygon(point);

        deliveryAddressStore.setMapCenter(point);
        deliveryAddressStore.setSelectedZone(selectedZone);

        deliveryAddressStore.setDeliveryAddress({
          zip: editableAddress.postcode || '',
          country: editableAddress.country_code || '',
          city: editableAddress.city || '',
          region: '',
          address1: editableAddress.street_address_1 || '',
          address2: editableAddress.street_address_2 || '',
          addressFloor: editableAddress.floor || '',
          addressBld: editableAddress.building || '',
          addressApt: editableAddress.apartment || '',
          shortAddress: editableAddress.street_address_1 || '',
          coordinates: point,
          placeId: editableAddress.place_id || '',
          addressId: editableAddress.id,
          comment: editableAddress.comment || '',
          instructions: editableAddress.instructions || [],
          type:
            editableAddress.type ||
            (company.canDefaultAddressBeEmpty ? '' : 'home'),
        });
        deliveryAddressStore.setEditAddressData({
          addressId: editableAddress.id,
          address2: editableAddress.street_address_2 || '',
          addressBld: editableAddress.building || '',
          addressFloor: editableAddress.floor || '',
          addressApt: editableAddress.apartment || '',
          type:
            editableAddress.type ||
            (company.canDefaultAddressBeEmpty ? '' : 'home'),
          comment: editableAddress.comment || '',
          instructions: editableAddress.instructions || [],
        });

        deliveryAddressStore.setInputAddressValue(
          `${editableAddress.street_address_1}, ${editableAddress.city}`,
        );

        const cleanTimeout = () => {
          if (geocodingRequestTimeoutRef.current) {
            clearTimeout(geocodingRequestTimeoutRef.current);
            geocodingRequestTimeoutRef.current = null;
          }
        };
        if (selectedZone) {
          cleanTimeout();
          geocodingRequestTimeoutRef.current = setTimeout(() => {
            deliveryAddressStore.geocoding(false, {
              lng: point.lng,
              lat: point.lat,
            });
            geocodingRequestTimeoutRef.current = null;
          }, 100);
        } else {
          orderStore.requestETA();
        }
      }
    }
  }, [editableAddressId, userStore.addressList]);

  useEffect(() => {
    return () => {
      if (!deliveryAddressStore.deliveryAddress) {
        deliveryAddressStore.resetStore();
      }
    };
  }, []);

  const handleCloseModal = () => {
    closeModal([MODAL_EDIT_ADDRESS_PARAM, REDIRECT_QUERY_NAME]);
  };

  const handleChangeLocationName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value && value.trim().toLowerCase() === 'home') {
      deliveryAddressStore.setAddressType('home');
    } else {
      deliveryAddressStore.setAddressTypeValue(value);
    }
  };

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    deliveryAddressStore.setDeliveryAddressComment(e.target.value);
  };

  const getFormattedType = (type: string) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const locationInput = (
    <Input
      className={styles.inputLocationName}
      size="S"
      rounded
      variant="transparent"
      label={t('checkout:locationName')}
      data-input="address-custom-name"
      placeholder={t('addressCustomName')}
      defaultValue={
        deliveryAddressStore.editAddressData.type?.toLowerCase() === 'home'
          ? getFormattedType(deliveryAddressStore.editAddressData.type)
          : ''
      }
      value={getFormattedType(deliveryAddressStore.editAddressData.type)}
      onChange={handleChangeLocationName}
    />
  );

  const handleGoBack = () => {
    goBack([MODAL_EDIT_ADDRESS_PARAM]);
    deliveryAddressStore.setSelectedZone(null);
  };

  const title = editableAddressId ? t('editAddress') : t('deliveryDetails');
  return (
    <Modal
      isShow
      className={styles.addressDetails}
      onBackdropDismiss={handleCloseModal}
    >
      <div className={styles.addressDetails__container}>
        <div className={styles.header}>
          <ModalHeader
            variant={isMobile ? 'dark' : 'default'}
            onClose={!isMobile ? closeModal : undefined}
            title={title}
          />
        </div>

        <ModalBody className={styles.addressDetails__container__body}>
          <div className={styles.addressDetails__container__body__content}>
            {isMobile ? null : (
              <div className={styles.inputGroup}>{locationInput} </div>
            )}

            <div className={styles.inputGroup}>
              <Input
                size="S"
                rounded
                variant="transparent"
                label={`${t('checkout:bld')}${company.isBuildingOptional ? '' : '*'}`}
                data-input="address-building"
                placeholder={t('checkout:enterBld')}
                value={deliveryAddressStore.editAddressData.addressBld}
                onChange={(e) =>
                  deliveryAddressStore.setDeliveryAddressBld(e.target.value)
                }
              />
              <Input
                size="S"
                rounded
                variant="transparent"
                label={t('checkout:floor')}
                data-input="address-floor"
                placeholder={t('checkout:enterFloor')}
                value={deliveryAddressStore.editAddressData.addressFloor}
                onChange={(e) =>
                  deliveryAddressStore.setDeliveryAddressFloor(e.target.value)
                }
              />
              <Input
                size="S"
                rounded
                variant="transparent"
                label={t('checkout:apt')}
                data-input="address-apt"
                placeholder={t('checkout:enterApt')}
                value={deliveryAddressStore.editAddressData.addressApt}
                onChange={(e) =>
                  deliveryAddressStore.setDeliveryAddressApt(e.target.value)
                }
              />
            </div>
            <div className={styles.instructionsContainer}>
              <Input
                as="textarea"
                size="S"
                variant="transparent"
                rounded
                className={styles.textArea}
                label={t('checkout:commentForCourierOptional')}
                placeholder={t('checkout:yourComment')}
                onChange={handleCommentChange}
                value={deliveryAddressStore.editAddressData.comment}
                rows={1}
              />

              <DeliveryInstructions classNames={styles.instructions} />
            </div>
          </div>

          <div className={styles.addressDetails__container__body__map}>
            <MapStatic />
          </div>

          {isMobile && locationInput}
        </ModalBody>

        {isMobile ? (
          <ModalFooter
            onBack={handleGoBack}
            primaryAction={{
              text: t('confirm'),
              onClick: handleSaveAddressMobile,
              disabled: isDisabled,
              isLoading,
            }}
          />
        ) : (
          <div className={styles.footer}>
            <SaveButton
              onClick={handleAfterSave}
              className={styles.proceedButton}
              title={t('confirm')}
              shouldValidateBuilding={company.isBuildingOptional}
            />
          </div>
        )}
      </div>
    </Modal>
  );
});

export default ModalEditAddressDetailsV3;
